import { useMemo } from "react";
import type { ButtonProps, FlexProps, IconProps, TextProps } from "@chakra-ui/react";
import { Button } from "@chakra-ui/react";
import { Flex, Icon, Text, useMultiStyleConfig, useTheme } from "@chakra-ui/react";
import { HiChevronLeft } from "react-icons/hi";
import { MdClose } from "react-icons/md";
import type { AppLinkProps } from "@/components/AppLink/AppLink";
import { AppLink } from "@/components/AppLink/AppLink";
import { FlexColumn } from "@/components/FlexColumn/FlexColumn";
import { FlexRow } from "@/components/FlexRow/FlexRow";
import { isParentHandlingEvent, postMessageToParent } from "@/services/integration";
import { htmlify } from "@/services/utils";

export interface FundDetailTopNavigationProps extends FlexProps {
	linkLabel: string;
	href: string;
	title: string;
	subTitle?: string;
	hideBackButton: boolean;
	hideCloseButton: boolean;
	fontSize: number;
	isEnabled?: boolean;
}

export function FundDetailTopNavigation({
	linkLabel,
	href,
	title,
	subTitle,
	children,
	hideBackButton,
	hideCloseButton,
	fontFamily,
	fontSize
}: FundDetailTopNavigationProps) {
	const { fonts } = useTheme();
	const styleParts = useMultiStyleConfig("FundDetailTopNavigation");
	const stylePartsCollection = useMultiStyleConfig("CollectionManager");

	const backButton = useMemo(() => {
		if (hideBackButton || linkLabel === undefined || href == undefined) {
			return null;
		}
		return (
			<>
				<AppLink href={href} {...(styleParts.backButton as AppLinkProps)}>
					<Icon as={HiChevronLeft} fontSize={26} {...(styleParts.backButtonIcon as IconProps)} />
					<Text
						color="inherit"
						marginLeft={0}
						variant="label"
						fontWeight="bold"
						display={{
							base: "none",
							lg: "inline-block"
						}}
						{...(styleParts.backButtonText as TextProps)}
					>
						{htmlify(linkLabel)}
					</Text>
				</AppLink>
				<FlexColumn {...(styleParts.backButtonSeparator as FlexProps)} />
			</>
		);
	}, [
		hideBackButton,
		href,
		linkLabel,
		styleParts.backButton,
		styleParts.backButtonIcon,
		styleParts.backButtonSeparator,
		styleParts.backButtonText
	]);

	const closeButton = useMemo(() => {
		if (hideCloseButton !== false) {
			return null;
		}

		return (
			<Button
				variant="noStyle"
				as="button"
				alignItems="center"
				cursor="pointer"
				color="shade.100"
				paddingX={2}
				fontSize={46}
				minWidth={62}
				onClick={() => {
					if (isParentHandlingEvent("CloseDFS")) {
						postMessageToParent({ type: "CloseDFS", content: "Close Modal" });
						return;
					}
					return false;
				}}
				aria-label="Close modal"
				{...(stylePartsCollection.closeButton as ButtonProps)}
			>
				<Icon as={MdClose} />
			</Button>
		);
	}, [hideCloseButton, stylePartsCollection.closeButton]);

	return (
		<FlexRow gap={3} data-testid="fundDetailTopNavigation" paddingLeft={3} width="100%" {...(styleParts.root as FlexProps)}>
			{backButton}
			<FlexColumn justifyContent="center" flex={1} minWidth="160px" {...(styleParts.itemsContainer as FlexProps)}>
				<Text
					fontSize={{ base: "16px", md: fontSize }}
					fontFamily={fonts.heading}
					variant="largeLabel"
					{...(styleParts.heading as TextProps)}
				>
					{htmlify(title)}
				</Text>
			</FlexColumn>
			<Flex marginLeft="auto">{children}</Flex>
			{closeButton}
		</FlexRow>
	);
}
